import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { type AppFilterFn } from "../../table/tableFeatures";
import { Flex, HStack, VStack } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import { Table } from "../../table/Table";
import { Button } from "../../core/basic/Button";
import { FaCheck, FaClone } from "react-icons/fa6";
import { VideoGenerationDialogueForm } from "./VideoGenerationDialogue";
import { ReviewModal } from "./ReviewModal";
import { SearchInput } from "../../fields/base/TextField";
import { Select } from "../../fields/base/Select";
import { Switch } from "../../fields/base/Switch";
import moment from "moment";
import { Badge } from "../../core/basic/ButtonGroup";
import { RT8Pagination } from "../../table/Pagination";
import { resetTable } from "../../table/utils";
import { MdRotateLeft } from "react-icons/md";
import { RT8ColumnSelector } from "../../core/blocks/MetricSelector";
import type { VideoGenerationTaskResult } from "../../../zodios/apiDefinitions/genai";
import type { FormValues } from "./videoGenFormSchema";
import { HoverInfo } from "../../core/basic/Tooltip";
import { useMutation } from "@tanstack/react-query";
import { apiClient } from "../../../zodios/client";
import { errorToast, successToast } from "../../../utils/toasts";
import { queryClient } from "../../../config/react-query";
import ExternalProductLink from "../merchandise/ExternalProductLink";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import { createAppColumnHelper, useClientTable } from "../../table/tableFeatures";

export const TIME_TO_REGISTER_ASSETS = 6;

type Props = {
  requestsData: VideoGenerationTaskResult[];
};

const columnHelper = createAppColumnHelper<VideoGenerationTaskResult>();

const stateFilterFn: AppFilterFn<VideoGenerationTaskResult> = (row, columnId, filterValue) => {
  if (!filterValue || filterValue === "all") {
    return true;
  }

  return row.getValue(columnId) === filterValue;
};

export const VideoGenerationRequestsTable = ({ requestsData }: Props) => {
  const { t } = useTranslation(["advertising", "common"]);
  const dateTimeFmt = useDateFormatter("shortDateTime");
  const [showModal, setShowModal] = useState(false);
  const [cloneRequestData, setCloneRequestData] = useState<null | FormValues>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewRequest, setReviewRequest] = useState<VideoGenerationTaskResult | null>(null);
  const [showReviewed, setShowReviewed] = useState(false);

  const stateFilterOptions = useMemo(
    () => [
      { value: "all", label: t(($) => $.advertising["All States"]) },
      { value: "PENDING", label: t(($) => $.advertising["Pending"]) },
      { value: "IN_PROGRESS", label: t(($) => $.advertising["In Progress"]) },
      { value: "SUCCESS", label: t(($) => $.advertising["Success"]) },
      { value: "FAILED", label: t(($) => $.advertising["Failed"]) },
      { value: "CANCELLED", label: t(($) => $.advertising["Cancelled"]) },
      { value: "RETRY", label: t(($) => $.advertising["Retry"]) },
      { value: "REJECTED", label: t(($) => $.advertising["Rejected"]) },
    ],
    [t],
  );

  const markAsReviewedMutation = useMutation({
    mutationFn: async (resultId: number) => {
      return await apiClient.updateVideoGenerationResult({ reviewed: true }, { params: { id: resultId } });
    },
    onSuccess: async () => {
      successToast({ message: t(($) => $.advertising["Marked as reviewed"]) });
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const stringifiedQueryKey = JSON.stringify(query.queryKey);
          return stringifiedQueryKey.includes("videoGenTaskResults");
        },
      });
    },
    onError: (error) => {
      errorToast({
        message: t(($) => $.advertising["Something went wrong while marking as reviewed. Please try again."]),
        error,
      });
    },
  });

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("id", {
        header: t(($) => $.advertising["ID"]),
        cell: (info) => info.getValue(),
        size: 80,
        enableHiding: false,
        sortFn: (rowA, rowB, columnId) => {
          const aValue = rowA.getValue<number>(columnId);
          const bValue = rowB.getValue<number>(columnId);
          const aIsActionable =
            rowA.original.state === "SUCCESS" &&
            !rowA.original.registered &&
            Boolean(rowA.original.completed_at) &&
            moment().diff(moment(rowA.original.completed_at), "hours", true) < TIME_TO_REGISTER_ASSETS;
          const bIsActionable =
            rowB.original.state === "SUCCESS" &&
            !rowB.original.registered &&
            Boolean(rowB.original.completed_at) &&
            moment().diff(moment(rowB.original.completed_at), "hours", true) < TIME_TO_REGISTER_ASSETS;

          if (aIsActionable !== bIsActionable) {
            return aIsActionable ? 1 : -1;
          }

          return aValue - bValue;
        },
      }),

      columnHelper.accessor("task.name", {
        header: t(($) => $.advertising["Name"]),
        cell: ({ row }) => {
          const status = row.original.state;
          const tooltip =
            status === "SUCCESS" ?
              t(($) => $.advertising["Review and Register Generated Videos"])
            : t(($) => $.advertising["Review Request Details"]);
          const completedAt = row.original.completed_at;
          const isWithinRegistrationWindow =
            status === "SUCCESS" && moment().diff(moment(completedAt), "hours", true) < TIME_TO_REGISTER_ASSETS;
          const canOpenReviewModal = status !== "SUCCESS" || isWithinRegistrationWindow;
          const assetsReadyForReview =
            row.original.state === "SUCCESS" && !row.original.registered && isWithinRegistrationWindow;
          const assetsExpired =
            row.original.state === "SUCCESS" && !row.original.registered && !isWithinRegistrationWindow;

          return (
            <VStack className={css({ paddingY: "medium" })} gap="xsmall" alignItems="start">
              {canOpenReviewModal ?
                <Button
                  variant="link"
                  tooltip={tooltip}
                  onPress={() => {
                    setReviewRequest(row.original);
                    setShowReviewModal(true);
                  }}
                  css={{ padding: 0, textDecoration: "none", textAlign: "left" }}
                >
                  {row.original.task.name}
                </Button>
              : <span className={css({ textAlign: "left" })}>{row.original.task.name}</span>}
              {assetsReadyForReview && (
                <Badge className={css({ whiteSpace: "nowrap" })} colorScheme="green">
                  {t(($) => $.advertising["Assets Ready for Review"])}
                </Badge>
              )}
              {assetsExpired && (
                <HoverInfo
                  icon={
                    <Badge className={css({ whiteSpace: "nowrap" })} colorScheme="gray">
                      {t(($) => $.advertising["Expired"])}
                    </Badge>
                  }
                >
                  <p className={css({ color: "white", fontSize: "medium" })}>
                    {t(
                      ($) =>
                        $.advertising[
                          "The registration window for the generated assets from this request has expired. Amazon allows assets to be registered within {hours} hours of video generation completion. Please review the generated assets and register them on Amazon as soon as possible after receiving the notification that they are ready for review."
                        ],
                      { hours: TIME_TO_REGISTER_ASSETS },
                    )}
                  </p>
                </HoverInfo>
              )}
            </VStack>
          );
        },
        size: 350,
      }),
      columnHelper.accessor("state", {
        header: t(($) => $.advertising["State"]),
        cell: (info) => {
          const value = info.getValue();
          switch (value) {
            case "PENDING":
              return t(($) => $.advertising["Pending"]);
            case "IN_PROGRESS":
              return t(($) => $.advertising["In Progress"]);
            case "SUCCESS":
              return t(($) => $.advertising["Success"]);
            case "FAILED":
              return t(($) => $.advertising["Failed"]);
            case "CANCELLED":
              return t(($) => $.advertising["Cancelled"]);
            case "RETRY":
              return t(($) => $.advertising["Retry"]);
            case "REJECTED":
              return t(($) => $.advertising["Rejected"]);
            default:
              return value;
          }
        },
        size: 160,
        filterFn: stateFilterFn,
      }),
      columnHelper.accessor("task.gvproduct.asin", {
        header: t(($) => $.advertising["ASIN"]),
        cell: (info) => {
          const asin = info.getValue();
          return (
            <span>
              {asin} <ExternalProductLink asin={asin} />
            </span>
          );
        },
        size: 150,
      }),
      columnHelper.accessor("completed_at", {
        header: t(($) => $.advertising["Completed At"]),
        cell: (info) => {
          const value = info.getValue();
          if (!value) {
            return "—";
          }
          return dateTimeFmt.format(moment(value).toDate());
        },
        size: 200,
        sortFn: (rowA, rowB, columnId) => {
          const aValue = rowA.getValue<string | null>(columnId);
          const bValue = rowB.getValue<string | null>(columnId);

          if (!aValue && !bValue) {
            return 0;
          }
          if (!aValue) {
            return -1;
          }
          if (!bValue) {
            return 1;
          }

          return new Date(aValue).getTime() - new Date(bValue).getTime();
        },
      }),
      columnHelper.accessor("registered", {
        header: t(($) => $.advertising["Assets Registered"]),
        cell: (info) => {
          const value = info.getValue();
          return value ? t(($) => $.common.Yes) : t(($) => $.common.No);
        },
        size: 100,
      }),
      columnHelper.accessor("reviewed_at", {
        header: t(($) => $.advertising["Reviewed"]),
        cell: (info) => {
          const value = info.getValue();
          return value ? t(($) => $.common.Yes) : t(($) => $.common.No);
        },
        size: 100,
        sortFn: (rowA, rowB, columnId) => {
          const aValue = rowA.getValue<string | null>(columnId);
          const bValue = rowB.getValue<string | null>(columnId);

          if (!aValue && !bValue) {
            return 0;
          }
          if (!aValue) {
            return -1;
          }
          if (!bValue) {
            return 1;
          }

          return new Date(aValue).getTime() - new Date(bValue).getTime();
        },
      }),
      columnHelper.display({
        id: "assets",
        header: t(($) => $.advertising["Inspirational Assets"]),
        cell: ({ row }) => {
          const imageCount = row.original.task.image_ids?.length ?? 0;
          const videoCount = row.original.task.video_ids?.length ?? 0;
          return (
            <HStack gap="medium">
              <span>{t(($) => $.advertising["Images: {count}"], { count: imageCount })}</span>
              <span>{t(($) => $.advertising["Videos: {count}"], { count: videoCount })}</span>
            </HStack>
          );
        },
        minSize: 220,
      }),
      columnHelper.display({
        id: "actions",
        header: t(($) => $.advertising["Actions"]),
        cell: ({ row }) => {
          const status = row.original.state;
          const requestAsin = row.original.task.gvproduct.asin;
          const requestProductId = row.original.task.gvproduct.id;
          const tooltip =
            status === "SUCCESS" ?
              t(($) => $.advertising["Review and Register Generated Videos"])
            : t(($) => $.advertising["Review Request Details"]);
          const completedAt = row.original.completed_at;
          const isWithinRegistrationWindow =
            status === "SUCCESS" &&
            Boolean(completedAt) &&
            moment().diff(moment(completedAt), "hours", true) < TIME_TO_REGISTER_ASSETS;

          return (
            <Flex gap={"large"}>
              {!row.original.reviewed_at && row.original.state === "SUCCESS" && (
                <Button
                  variant="contentOnly"
                  aria-label={t(($) => $.advertising["Mark as Reviewed"])}
                  onPress={() => markAsReviewedMutation.mutate(row.original.id)}
                  isDisabled={markAsReviewedMutation.isPending && markAsReviewedMutation.variables === row.original.id}
                  tooltip={t(($) => $.advertising["Mark as Reviewed"])}
                >
                  <FaCheck />
                </Button>
              )}
              <Button
                variant="contentOnly"
                aria-label={t(($) => $.advertising["Duplicate {name}"], { name: row.original.task.name })}
                onPress={() => {
                  setCloneRequestData({
                    asin: requestAsin,
                    productId: requestProductId,
                    enableInspirationAssetSelection: Boolean(
                      row.original.task.video_ids?.length || row.original.task.image_ids?.length,
                    ),
                    name: t(($) => $.advertising["{name} (copy)"], { name: row.original.task.name }),
                    brandLogoAssetIds: [],
                    lifestyleImageAssetIds: row.original.task.image_ids ?? [],
                    videoAssetIds: row.original.task.video_ids ?? [],
                  });
                  setShowModal(true);
                }}
                tooltip={t(($) => $.advertising["Duplicate Video Generation Request"])}
              >
                <FaClone style={{ fontSize: "14px", color: "#777" }} />
              </Button>
              {row.original.state === "SUCCESS" && isWithinRegistrationWindow && (
                <Button
                  size="small"
                  aria-label={tooltip}
                  onPress={() => {
                    setReviewRequest(row.original);
                    setShowReviewModal(true);
                  }}
                  tooltip={tooltip}
                >
                  {t(($) => $.advertising["Review"])}
                </Button>
              )}
            </Flex>
          );
        },
        meta: { justify: "end", headerJustify: "end" },
        maxSize: 220,
        minSize: 220,
      }),
    ];
  }, [dateTimeFmt, markAsReviewedMutation, t]);

  const filteredData = useMemo(() => {
    if (showReviewed) {
      return requestsData;
    }
    return requestsData.filter((r) => r.reviewed_at === null);
  }, [requestsData, showReviewed]);

  const table = useClientTable({
    data: filteredData,
    columns,
    columnResizeMode: "onChange",
    enableGlobalFilter: true,
    globalFilterFn: (row, columnId, filterValue) => {
      if (!filterValue) {
        return true;
      }
      return row.original.task.name.toLowerCase().includes(filterValue.toLowerCase());
    },
    initialState: {
      sorting: [{ id: "id", desc: true }],
      columnVisibility: {
        id: false,
        assets: false,
      },
      pagination: { pageIndex: 0, pageSize: 5 },
    },
  });

  return (
    <VStack alignItems="stretch">
      {showReviewModal && reviewRequest && (
        <ReviewModal
          isOpen={showReviewModal}
          result={reviewRequest}
          onClose={() => {
            setShowReviewModal(false);
            setReviewRequest(null);
          }}
        />
      )}
      {showModal && cloneRequestData && (
        <VideoGenerationDialogueForm
          asin={cloneRequestData.asin}
          productId={cloneRequestData.productId}
          showModal={true}
          setShowModal={setShowModal}
          initialData={cloneRequestData}
        />
      )}
      <HStack gap="medium" justifyContent={"center"} alignItems="flex-end" paddingBottom="medium">
        <HStack gap="medium" alignItems="center">
          <Button
            variant={"contentOnly"}
            onPress={() => {
              resetTable(table);
            }}
            fontSize={"xxlarge"}
          >
            <MdRotateLeft />
          </Button>
          <RT8ColumnSelector table={table} />
          <SearchInput
            aria-label={t(($) => $.advertising["Search requests"])}
            placeholder={t(($) => $.advertising["Search name, assets, registration, completed time..."])}
            value={table.state.globalFilter ?? ""}
            onChange={table.setGlobalFilter}
            css={{ flex: { base: "1 1 350px", md: "1 1 400px" } }}
          />
          <Select
            aria-label={t(($) => $.advertising["State filter"])}
            items={stateFilterOptions}
            selectedKey={(table.getColumn("state")?.getFilterValue() as string | undefined) ?? "all"}
            onSelectionChange={(value) => {
              table.getColumn("state")?.setFilterValue(value === "all" ? undefined : value);
            }}
            css={{ minWidth: "180px" }}
          />
          <Switch isSelected={showReviewed} onChange={setShowReviewed}>
            <label className={css({ whiteSpace: "nowrap" })}>
              {t(($) => $.advertising["Include Reviewed Results"])}
            </label>
          </Switch>
        </HStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      {table.getRowModel().rows.length === 0 ?
        <Flex
          justifyContent="center"
          alignItems="center"
          paddingTop="medium"
          className={css({
            color: "grey",
            fontSize: "medium",
          })}
        >
          {t(($) => $.advertising["No video generation requests match the selected filters."])}
        </Flex>
      : <Table table={table} />}
    </VStack>
  );
};
